import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';

const EventsListModal = ({ isOpen, onClose, onSelectEvent }) => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEventId, setSelectedEventId] = useState(null);

  // Handle ESC key press
  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);
  
  // Add event listener for ESC key
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      fetchEvents();
    }
    
    // Clean up
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, page, handleEscapeKey]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // We'll use the regular event endpoint without date range filters
      // This will fetch ALL events regardless of date (even those from 1970!)
      const response = await axios.get('/api/calendar', {
        headers: {
          'x-auth-token': token
        },
        // Important: Don't include start_date and end_date to get all events
        params: {
          search: searchTerm !== '' ? searchTerm : undefined
        }
      });
      
      // Sort the events by date (most recent first) - helpful for finding accidental old events
      const sortedEvents = [...response.data].sort((a, b) => 
        new Date(b.start_time) - new Date(a.start_time)
      );
      
      setEvents(sortedEvents);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEvents();
  };

  const goToEvent = () => {
    if (selectedEventId) {
      const selectedEvent = events.find(event => event.id === selectedEventId);
      if (selectedEvent) {
        // Close the modal
        onClose();
        
        // Callback to select the event in the calendar
        onSelectEvent(selectedEvent);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium">Find Calendar Events</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="px-6 py-4 border-b border-gray-200">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              placeholder="Search events by title, description, location..."
              className="flex-1 border border-gray-300 rounded px-3 py-2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Search
            </button>
          </form>
        </div>
        
        <div className="overflow-auto flex-1 p-6">
          {loading ? (
            <div className="text-center py-4">Loading events...</div>
          ) : events.length === 0 ? (
            <div className="text-center py-4">No events found</div>
          ) : (
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b text-left">Title</th>
                  <th className="py-2 px-4 border-b text-left">Date & Time</th>
                  <th className="py-2 px-4 border-b text-left">Location</th>
                  <th className="py-2 px-4 border-b text-left">Type</th>
                  <th className="py-2 px-4 border-b text-left">Country</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <tr 
                    key={event.id}
                    onClick={() => setSelectedEventId(event.id)}
                    className={`cursor-pointer hover:bg-gray-50 ${selectedEventId === event.id ? 'bg-blue-50' : ''}`}
                  >
                    <td className="py-2 px-4 border-b">{event.title}</td>
                    <td className="py-2 px-4 border-b">
                      {moment(event.start_time).format('YYYY-MM-DD HH:mm')} - 
                      {moment(event.end_time).format('HH:mm')}
                    </td>
                    <td className="py-2 px-4 border-b">{event.location || '-'}</td>
                    <td className="py-2 px-4 border-b">
                      <span className={`px-2 py-1 rounded-full text-xs
                        ${event.event_type === 'course' ? 'bg-green-100 text-green-800' : ''}
                        ${event.event_type === 'demo' ? 'bg-blue-100 text-blue-800' : ''}
                        ${event.event_type === 'sales call' ? 'bg-amber-100 text-amber-800' : ''}
                      `}>
                        {event.event_type}
                      </span>
                    </td>
                    <td className="py-2 px-4 border-b">{event.country || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className={`px-3 py-1 rounded ${page === 1 ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className={`px-3 py-1 rounded ${page === totalPages ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Next
            </button>
          </div>
          
          <button
            onClick={goToEvent}
            disabled={!selectedEventId}
            className={`px-4 py-2 rounded ${!selectedEventId ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            Go to Selected Event
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventsListModal;